import { useEffect, useState, useRef } from 'react';
import { View, Pressable, Text, StyleSheet, Platform, Alert, Animated } from 'react-native';
import * as WebBrowser from "expo-web-browser";
import { makeRedirectUri, useAuthRequest, useAutoDiscovery } from 'expo-auth-session';
import styles from '@stylesheet'; // Assuming this is a valid import from your project structure
import { LinearGradient } from 'expo-linear-gradient';
import DiscordIcon from '@assets/--native/Icons/discord-icon'; // Assuming this is a valid import
import { getAuthSessionGuard } from '@utils/authSessionGuard';
import { useDispatch } from 'react-redux';
import { SHOW_AUTH_MODAL } from '@reduxLocal/actions/types';

// This should be called at the root of your application, but it's fine here for a self-contained component.
WebBrowser.maybeCompleteAuthSession();

// Get auth session guard singleton
const authGuard = getAuthSessionGuard();

const DiscordButton = ({ code, productId }: { code?: string; productId?: string }) => {
  const dispatch = useDispatch();
  const [token, setToken] = useState("");
  const [userInfo, setUserInfo] = useState(null);
  // Animation for code indicator
  const fadeAnim = useRef(new Animated.Value(0)).current;

  // Animation effect for code indicator
  useEffect(() => {
    if (code) {
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 1000,
        useNativeDriver: true,
      }).start();
    }
  }, [code, fadeAnim]);

  // Automatically discover the Discord authentication endpoints.
  // Note: useAutoDiscovery automatically appends .well-known/openid-configuration, so we only need the base URL
  const discovery = useAutoDiscovery('https://discord.com');

  // Create the auth request hook.
  const [request, response, promptAsync] = useAuthRequest(
    {
      clientId: process.env.EXPO_PUBLIC_DISCORD_CLIENTID,
      scopes: ['openid', 'profile', 'email', 'identify'],
      // Use makeRedirectUri for a more robust redirect URI handling across different environments.
      redirectUri: makeRedirectUri({
        // For usage in Expo Go.
        native: 'your-app-scheme://auth',
        // You can add other platforms like 'web' if needed.
      }),
    },
    discovery
  );

  // This effect handles the response from the authentication service.
  useEffect(() => {
    if (response) {
      if (response.type === "success") {
        const { access_token } = response.params;
        setToken(access_token);
      } else if (response.type === "error") {
        Alert.alert('Authentication Error', response.params.error_description || 'Something went wrong');
      }
    }
  }, [response]);

  // This effect fetches user info once an access token is available.
  useEffect(() => {
    if (!token) return;

    const fetchUserInfo = async () => {
      try {
        const result = await fetch('https://discord.com/api/users/@me', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const user = await result.json();
        setUserInfo(user);
        // You can now use the user info (e.g., navigate, update state)
        console.log(user);
      } catch (error) {
        console.error("Failed to fetch user info:", error);
        Alert.alert('Error', 'Failed to fetch user information.');
      }
    };

    fetchUserInfo();
  }, [token]);

  const handleClick = async () => {
    // Check if another auth session is active
    if (authGuard.isSessionActive()) {
      const activeSession = authGuard.getActiveSession();
      console.warn(`Cannot start Discord auth - ${activeSession} is already active`);
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: {
          content: `Please wait for ${activeSession} authentication to complete`,
          type: 'error'
        }
      });
      return;
    }

    // Start auth session with guard
    await authGuard.startSession('Discord', async () => {
      return await promptAsync();
    });
  };

  return (
    <Pressable
      disabled={!request}
      onPress={handleClick}
      style={componentStyles.pressable}
    >
      {({ hovered }) => (
        <View style={componentStyles.container}>
          {/* Animated gradient background for code indicator */}
          {code && (
            <Animated.View
              style={{
                ...StyleSheet.absoluteFillObject,
                opacity: fadeAnim,
                borderRadius: 10,
              }}
            >
              <LinearGradient
                colors={['#E4D337', '#FF5733']}
                style={{ flex: 1, borderRadius: 10 }}
              />
            </Animated.View>
          )}

          <View style={[componentStyles.innerView, { backgroundColor: hovered && !userInfo ? 'rgba(0,0,0,0.1)' : 'transparent' }]}>
            <View style={componentStyles.iconContainer}>
              <DiscordIcon width={25} height={25} />
            </View>
            <View style={componentStyles.textContainer}>
              <Text style={componentStyles.text}>
                {userInfo ? `Welcome, ${userInfo.username}!` : 'Sign in with Discord'}
              </Text>
            </View>
          </View>
        </View>
      )}
    </Pressable>
  );
}

// Moved styles into a StyleSheet for better organization and performance.
const componentStyles = StyleSheet.create({
  pressable: {
    minHeight: 50,
    height: 50,
    minWidth: '100%',
    maxHeight: 50,
    flex: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
    backgroundColor: 'white',
    borderRadius: 30,
    overflow: 'hidden',
  },
  container: {
    flex: 1,
    minHeight: 45,
  },
  innerView: {
    flex: 1,
    paddingHorizontal: '10%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconContainer: {
    flex: 1,
    maxWidth: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  textContainer: {
    flex: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },
  text: {
    color: 'black',
    fontWeight: '600',
  },
});


export default DiscordButton;